import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { featureTemplateService } from "@/services/featureTemplateService";
import { schemaBuilderService } from "@/services/schemaBuilderService";
import { campaignService } from "@/services/campaignService";
import { SchemaField, SchemaTemplate } from "@/types/schemaBuilder";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrayFieldRenderer } from "./ArrayFieldRenderer";
import { Campaign } from "@/types/campaign";
import { FeatureTemplate } from "@/types/featureTemplate";
import { getPhoneNumbers } from "@/services/phoneNumberService";
import { PhoneNumber } from "@/models/PhoneNumber";
import { whatsappService, WhatsAppNumber } from "@/services/whatsappService";

interface CreateCampaignFormProps {
  featureTemplateId: string;
  organizationId: string;
  onClose: () => void;
  onSuccess: (campaign: Campaign) => void;
}

interface FormData {
  name: string;
  description?: string;
  phone_number_id: string; // Add phone_number_id to form data
  whatsapp_number_id?: string; // Add whatsapp_number_id to form data
  [key: string]: any;
}

export default function CreateCampaignForm({
  featureTemplateId,
  organizationId,
  onClose,
  onSuccess,
}: CreateCampaignFormProps) {
  const [schemaTemplate, setSchemaTemplate] = useState<SchemaTemplate | null>(null);
  const [featureTemplate, setFeatureTemplate] = useState<FeatureTemplate | null>(null);
  const [existingCampaignsCount, setExistingCampaignsCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(true);
  const [whatsappNumbers, setWhatsappNumbers] = useState<WhatsAppNumber[]>([]);
  const [loadingWhatsappNumbers, setLoadingWhatsappNumbers] = useState(true);

  const form = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      phone_number_id: "", // Add default value
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get the feature template
        const template = await featureTemplateService.getFeatureTemplate(featureTemplateId);
        if (!template) throw new Error("Feature template not found");
        setFeatureTemplate(template);

        // Get the schema template
        const schema = await schemaBuilderService.getTemplateById(
          template.schema_template_id
        );
        if (!schema) throw new Error("Schema template not found");
        setSchemaTemplate(schema);

        // Get count of existing campaigns for this template
        const { data: campaigns } = await campaignService.getCampaigns(organizationId);
        const count = campaigns?.filter(c => c.feature_template_id === featureTemplateId).length || 0;
        setExistingCampaignsCount(count);

        // Set default values from schema
        const defaultValues: Record<string, any> = {
          name: "",
          description: "",
        };

        Object.entries(schema.schema.properties).forEach(([key, field]) => {
          if ("default" in field) {
            defaultValues[key] = field.default;
          }
        });

        form.reset(defaultValues);
      } catch (error) {
        console.error("Error fetching schema:", error);
        toast({
          title: "Error",
          description: "Failed to load form schema",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [featureTemplateId, form]);

  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      try {
        setLoadingPhoneNumbers(true);
        const numbers = await getPhoneNumbers(organizationId);
        // Filter to only outbound numbers
        const outboundNumbers = numbers.filter(n => n.type === "outbound");
        setPhoneNumbers(outboundNumbers);
        
        // Auto-select the first number if available
        if (outboundNumbers.length > 0) {
          form.setValue("phone_number_id", outboundNumbers[0].id);
        }
      } catch (error) {
        console.error("Error fetching phone numbers:", error);
        toast({
          title: "Error",
          description: "Failed to load phone numbers",
          variant: "destructive",
        });
      } finally {
        setLoadingPhoneNumbers(false);
      }
    };

    const fetchWhatsAppNumbers = async () => {
      try {
        setLoadingWhatsappNumbers(true);
        const availableNumbers = await whatsappService.getAvailableWhatsAppNumbers(organizationId);
        setWhatsappNumbers(availableNumbers);
      } catch (error) {
        console.error("Error fetching WhatsApp numbers:", error);
        toast({
          title: "Error",
          description: "Failed to load WhatsApp numbers",
          variant: "destructive",
        });
      } finally {
        setLoadingWhatsappNumbers(false);
      }
    };

    fetchPhoneNumbers();
    fetchWhatsAppNumbers();
  }, [organizationId]);

  const renderSchemaField = (
    fieldName: string,
    field: SchemaField,
    parentPath: string = ""
  ) => {
    const fullPath = parentPath ? `${parentPath}.${fieldName}` : fieldName;
    const label = field.title || fieldName;
    const description = field.description;

    switch (field.type) {
      case "string":
        if (field.enum) {
          return (
            <FormField
              control={form.control}
              name={fullPath}
              rules={{ required: field.required }}
              render={({ field: formField }) => (
                <FormItem>
                  <FormLabel>{label}</FormLabel>
                  <Select
                    onValueChange={formField.onChange}
                    value={formField.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {field.enum.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {description && (
                    <FormDescription>{description}</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        }

        return (
          <FormField
            control={form.control}
            name={fullPath}
            rules={{ required: field.required }}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  {field.multiline ? (
                    <Textarea
                      placeholder={`Enter ${label.toLowerCase()}`}
                      {...formField}
                    />
                  ) : (
                    <Input
                      placeholder={`Enter ${label.toLowerCase()}`}
                      {...formField}
                    />
                  )}
                </FormControl>
                {description && (
                  <FormDescription>{description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case "number":
        return (
          <FormField
            control={form.control}
            name={fullPath}
            rules={{ required: field.required }}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder={`Enter ${label.toLowerCase()}`}
                    {...formField}
                    onChange={(e) =>
                      formField.onChange(e.target.valueAsNumber)
                    }
                  />
                </FormControl>
                {description && (
                  <FormDescription>{description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case "boolean":
        return (
          <FormField
            control={form.control}
            name={fullPath}
            rules={{ required: field.required }}
            render={({ field: formField }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={formField.value}
                    onCheckedChange={formField.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>{label}</FormLabel>
                  {description && (
                    <FormDescription>{description}</FormDescription>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case "object":
        if (!field.properties) return null;
        
        return (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">{label}</CardTitle>
              {description && (
                <FormDescription>{description}</FormDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(field.properties).map(([propName, propField]) => (
                <div key={propName}>
                  {renderSchemaField(propName, propField, fullPath)}
                </div>
              ))}
            </CardContent>
          </Card>
        );

      case "array":
        return (
          <ArrayFieldRenderer
            control={form.control}
            fullPath={fullPath}
            field={field}
            label={label}
            description={description}
            renderSchemaField={renderSchemaField}
          />
        );

      default:
        return null;
    }
  };

  const onSubmit = async (data: FormData) => {
    // Check instance limit
    if (featureTemplate?.max_instances !== undefined && featureTemplate?.max_instances !== null) {
      if (existingCampaignsCount >= featureTemplate.max_instances) {
        toast({
          title: "Limit Exceeded",
          description: `This template is limited to ${featureTemplate.max_instances} instance${featureTemplate.max_instances === 1 ? '' : 's'}. Please delete an existing campaign before creating a new one.`,
          variant: "destructive",
        });
        return;
      }
    }

    // Validate phone number
    if (!data.phone_number_id) {
      toast({
        title: "Error",
        description: "Please select an outbound phone number",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Separate campaign metadata from schema-based data
      const { name, description, phone_number_id, whatsapp_number_id, ...configurationData } = data;

      const result = await campaignService.createCampaign({
        name,
        description,
        organization_id: organizationId,
        feature_template_id: featureTemplateId,
        phone_number_id, // Include phone_number_id
        whatsapp_number_id: whatsapp_number_id === "none" ? null : whatsapp_number_id, // Include whatsapp_number_id
        status: "draft",
        configuration_data: configurationData,
      });

      if (result.error) throw result.error;
      if (!result.data) throw new Error("No data returned from creation");

      toast({
        title: "Success",
        description: "Campaign created successfully",
      });

      onSuccess(result.data);
    } catch (error) {
      console.error("Error creating campaign:", error);
      toast({
        title: "Error",
        description: "Failed to create campaign",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!schemaTemplate || !featureTemplate) {
    return (
      <div className="text-center py-8 text-destructive">
        Failed to load form schema
      </div>
    );
  }

  // Show warning if close to or at instance limit
  const showLimitWarning = featureTemplate.max_instances !== undefined && 
    featureTemplate.max_instances !== null && 
    existingCampaignsCount >= (featureTemplate.max_instances - 1);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {showLimitWarning && (
          <Card className="bg-mid-bg border-mid-line">
            <CardContent className="pt-6">
              <p className="text-mid">
                {existingCampaignsCount >= featureTemplate.max_instances
                  ? `This template has reached its maximum limit of ${featureTemplate.max_instances} instance${featureTemplate.max_instances === 1 ? '' : 's'}.`
                  : `This template is limited to ${featureTemplate.max_instances} instance${featureTemplate.max_instances === 1 ? '' : 's'}. You have ${featureTemplate.max_instances - existingCampaignsCount} remaining.`
                }
              </p>
            </CardContent>
          </Card>
        )}

        <FormField
          control={form.control}
          name="phone_number_id"
          rules={{ required: "Phone number is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Outbound Phone Number</FormLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={loadingPhoneNumbers}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a phone number" />
                </SelectTrigger>
                <SelectContent>
                  {phoneNumbers.map((number) => (
                    <SelectItem key={number.id} value={number.id}>
                      {number.phone_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {phoneNumbers.length === 0 && !loadingPhoneNumbers && (
                <p className="text-xs text-danger">
                  No outbound phone numbers available. Please add a phone number first.
                </p>
              )}
              <FormDescription>
                The phone number that will be used for outbound calls in this campaign
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="whatsapp_number_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>WhatsApp Number (Optional)</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                value={field.value || "none"}
                disabled={loadingWhatsappNumbers}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={
                      loadingWhatsappNumbers ? "Loading..." : "Select a WhatsApp number"
                    } />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {whatsappNumbers.map((number) => (
                    <SelectItem key={number.id} value={number.id}>
                      {number.phone_number} {number.display_name ? `(${number.display_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {whatsappNumbers.length === 0 && !loadingWhatsappNumbers && (
                <p className="text-xs text-muted-foreground">
                  No WhatsApp numbers available. Add one in organization settings.
                </p>
              )}
              <FormDescription>
                Optional WhatsApp number for this campaign. Once assigned, it cannot be changed.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          rules={{ required: "Name is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter campaign name" {...field} />
              </FormControl>
              <FormDescription>
                A descriptive name for your campaign
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter campaign description"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Optional description for your campaign
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {Object.entries(schemaTemplate.schema.properties).map(
          ([fieldName, field]) => (
            <div key={fieldName}>{renderSchemaField(fieldName, field)}</div>
          )
        )}

        <div className="flex justify-end gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting || (featureTemplate.max_instances !== undefined && 
              featureTemplate.max_instances !== null && 
              existingCampaignsCount >= featureTemplate.max_instances)}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Campaign"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}